import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/components/Auth/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { 
  getCollaborativeData, 
  getUserOrganizationRole, 
  checkCollaborativeAccess 
} from '@/utils/storageIsolation';
import { 
  Users, 
  TrendingUp, 
  Activity, 
  Calendar, 
  BarChart3,
  UserCheck,
  Clock,
  Target
} from 'lucide-react';

interface TeamActivity {
  id: string;
  user_name: string;
  action: string;
  data_type: string;
  timestamp: string;
  impact_score: number;
}

interface CollaborativeMetrics {
  totalTeamMembers: number;
  activeContributors: number;
  dataPointsAdded: number;
  reportsGenerated: number;
  averageActivityScore: number;
  completionRate: number;
}

export const CollaborativeAnalytics = () => {
  const [teamActivity, setTeamActivity] = useState<TeamActivity[]>([]);
  const [metrics, setMetrics] = useState<CollaborativeMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<string>('');
  const [organizationId, setOrganizationId] = useState<string>('');
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (user) {
      loadCollaborativeData();
    }
  }, [user]);

  const loadCollaborativeData = async () => {
    try {
      setLoading(true);

      // Get user's organization role
      const roleResult = await getUserOrganizationRole();
      if (roleResult.success && roleResult.organizationId) {
        setUserRole(roleResult.role || '');
        setOrganizationId(roleResult.organizationId);

        // Check if user has access to collaborative data
        const accessResult = await checkCollaborativeAccess(roleResult.organizationId, 'read');
        if (!accessResult.success || !accessResult.hasAccess) {
          toast({
            title: "Access Restricted",
            description: "You don't have permission to view collaborative analytics.",
            variant: "destructive"
          });
          return;
        }

        // Load team activity data
        const activityResult = await getCollaborativeData(roleResult.organizationId, 'team_activity');
        if (activityResult.success) {
          setTeamActivity(activityResult.data || []);
        }

        // Load collaborative metrics
        const metricsResult = await getCollaborativeData(roleResult.organizationId, 'collaborative_metrics');
        if (metricsResult.success && metricsResult.data && metricsResult.data.length > 0) {
          setMetrics(metricsResult.data[0]);
        } else {
          // Generate default metrics if none exist
          await generateDefaultMetrics(roleResult.organizationId);
        }
      }
    } catch (error) {
      console.error('Error loading collaborative data:', error);
      toast({
        title: "Error",
        description: "Failed to load collaborative analytics",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const generateDefaultMetrics = async (orgId: string) => {
    try {
      // Get team members count
      const { data: members } = await supabase
        .from('organization_members')
        .select('id, status')
        .eq('organization_id', orgId);

      const totalMembers = members?.length || 0;
      const activeMembers = members?.filter(m => m.status === 'active').length || 0;

      const defaultMetrics: CollaborativeMetrics = {
        totalTeamMembers: totalMembers,
        activeContributors: activeMembers,
        dataPointsAdded: 0,
        reportsGenerated: 0,
        averageActivityScore: 0,
        completionRate: 0
      };

      setMetrics(defaultMetrics);
    } catch (error) {
      console.error('Error generating default metrics:', error);
    }
  };

  const getActivityIcon = (action: string) => {
    switch (action.toLowerCase()) {
      case 'data_entry':
        return <BarChart3 className="h-4 w-4" />;
      case 'report_generated':
        return <TrendingUp className="h-4 w-4" />;
      case 'target_set':
        return <Target className="h-4 w-4" />;
      default:
        return <Activity className="h-4 w-4" />;
    }
  };

  const getImpactColor = (score: number) => {
    if (score >= 8) return 'bg-green-100 text-green-800';
    if (score >= 5) return 'bg-yellow-100 text-yellow-800';
    return 'bg-red-100 text-red-800';
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Collaborative Analytics
            </CardTitle>
            <CardDescription>
              Loading team insights and collaborative metrics...
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!organizationId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Collaborative Analytics
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>You need to be part of an organization to view collaborative analytics.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Collaborative Analytics
          </CardTitle>
          <CardDescription>
            Team insights and collaborative sustainability metrics
            {userRole && (
              <Badge variant="outline" className="ml-2">
                {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
              </Badge>
            )}
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Metrics Overview */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Team Members</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.totalTeamMembers}</div>
              <p className="text-xs text-muted-foreground">
                {metrics.activeContributors} active contributors
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Data Points</CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.dataPointsAdded}</div>
              <p className="text-xs text-muted-foreground">
                Added this month
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Reports</CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.reportsGenerated}</div>
              <p className="text-xs text-muted-foreground">
                Generated this quarter
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Activity Score</CardTitle>
              <Activity className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metrics.averageActivityScore.toFixed(1)}</div>
              <p className="text-xs text-muted-foreground">
                Average team activity
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Completion Progress */}
      {metrics && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Team Goals Progress
            </CardTitle>
            <CardDescription>
              Overall completion rate of sustainability objectives
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Sustainability Goals</span>
                <span className="text-sm text-muted-foreground">{metrics.completionRate}%</span>
              </div>
              <Progress value={metrics.completionRate} className="w-full" />
              <p className="text-xs text-muted-foreground">
                {metrics.completionRate >= 80 ? 'Excellent progress!' : 
                 metrics.completionRate >= 60 ? 'Good progress, keep it up!' : 
                 'More work needed to meet targets'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Team Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Team Activity
          </CardTitle>
          <CardDescription>
            Latest contributions and actions from team members
          </CardDescription>
        </CardHeader>
        <CardContent>
          {teamActivity.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No recent team activity</p>
              <p className="text-sm">Team members' actions will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {teamActivity.slice(0, 10).map((activity) => (
                <div key={activity.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {getActivityIcon(activity.action)}
                    <div>
                      <p className="font-medium">{activity.user_name}</p>
                      <p className="text-sm text-gray-600">
                        {activity.action.replace('_', ' ')} - {activity.data_type}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge className={getImpactColor(activity.impact_score)}>
                      Impact: {activity.impact_score}/10
                    </Badge>
                    <div className="text-sm text-gray-500">
                      <Clock className="h-3 w-3 inline mr-1" />
                      {new Date(activity.timestamp).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}; 